import type { JsBridgeSdk } from './jsBridge'
import type { Capabilities } from './capabilities'
import { resultError, resultOk, type Result } from '../result'
import type { DeviceApiClientName } from './clientName'
import log from 'loglevel'

export interface ClientInfoJsBridgeSdk {
  name: DeviceApiClientName
  version: string
  localization: string
}

export async function clientInfoFromJsBridgeSdk(
  jsBridgeSdk: Partial<JsBridgeSdk>
): Promise<ClientInfoJsBridgeSdk> {
  const [clientVersionResult, clientNameResult, clientLocalizationResult] =
    await Promise.allSettled([
      jsBridgeSdk?.getClientVersion?.() ?? '',
      jsBridgeSdk?.getClientName?.() ?? ('unknown' as const),
      jsBridgeSdk?.getClientLocalization?.() ?? ''
    ])

  let clientName: DeviceApiClientName = 'unknown'
  let clientVersion = ''
  let clientLocalization = ''

  if (clientVersionResult.status === 'fulfilled') {
    clientVersion = clientVersionResult.value
  } else {
    log.debug('failed to read client version from sdk', JSON.stringify(clientVersionResult.reason))
  }

  if (clientNameResult.status === 'fulfilled') {
    clientName = clientNameResult.value
  } else {
    log.debug('failed to read client name from sdk', JSON.stringify(clientNameResult.reason))
  }

  if (clientLocalizationResult.status === 'fulfilled') {
    clientLocalization = clientLocalizationResult.value
  } else {
    log.debug(
      'failed to read client localization from sdk',
      JSON.stringify(clientLocalizationResult.reason)
    )
  }

  return { name: clientName, version: clientVersion, localization: clientLocalization }
}

export class ApiNotSupportedError extends Error {
  constructor(methodName: string) {
    super()
    this.message = `Tried to use client API method '${methodName}' unsupported by client sdk implementation.`
  }
}

/**
 * Used when accessing Opera Rewards from client SDK webview.
 *
 * Specification:
 * https://docs.google.com/document/d/1cGBvMdMTQr6O97EX6g1NE2hfjpjXaQYtfczACm2Zu8E/edit?tab=t.w2fk5qk4mmbn
 */
export class DeviceApiSdk {
  readonly type = 'sdk'

  constructor(
    private jsBridge: Partial<JsBridgeSdk>,
    // Client APIs for version, name, and localization are synchronous in native
    // and web client API implementations, but the JsBridgeSdk provides them
    // asynchronously. To maintain a synchronous interface for ClientApiSdk, we
    // fetch these values once from the JsBridgeSdk during initialization
    // and store them as attributes. This simplifies our code by avoiding a
    // complete refactor to an asynchronous ClientApi, and it's a valid approach
    // since these client details are expected to remain constant after initial
    // load.
    private clientInfo: ClientInfoJsBridgeSdk
  ) {}

  getClientName(): DeviceApiClientName {
    return this.clientInfo.name
  }

  getClientNameAsResult(): Result<DeviceApiClientName> {
    try {
      return resultOk(this.getClientName())
    } catch (error: unknown) {
      return resultError(error)
    }
  }

  getClientLocalization(): string {
    return this.clientInfo.localization
  }

  getClientVersion(): string {
    return this.clientInfo.version
  }

  getCapabilities(): Capabilities {
    return {}
  }

  requestAccessToken(forceRefresh: boolean): void {
    throw new ApiNotSupportedError(`requestAccessToken("${forceRefresh}")`)
  }

  getAccessToken(forceRefresh: Boolean): Promise<string> {
    if (this.jsBridge.getAccessToken === undefined) {
      throw new ApiNotSupportedError(`getAccessToken(${forceRefresh})`)
    }

    return this.jsBridge.getAccessToken(forceRefresh)
  }

  onTokensChange(accessToken: string, refreshToken: string): void {
    throw new ApiNotSupportedError(`onTokensChange("${accessToken}", ${refreshToken})`)
  }

  onLogout(): void {
    throw new ApiNotSupportedError(`onLogout`)
  }

  getWalletAddress(): string | undefined {
    return undefined
  }

  openOperaMiniDeepLink(_deepLink: string, _close: boolean): void {
    throw new ApiNotSupportedError(`openOperaMiniDeepLink("${_deepLink}", ${_close})`)
  }

  close(): void {
    throw new ApiNotSupportedError('close')
  }

  requestSocialLogin(): void {
    throw new ApiNotSupportedError('requestSocialLogin')
  }

  getSocialAccount(): string | undefined {
    throw new ApiNotSupportedError('getSocialAccount')
  }

  socialSignOut(): void {
    throw new ApiNotSupportedError('socialSignOut')
  }

  async isDefaultBrowser(): Promise<boolean | undefined> {
    if (this.jsBridge.isDefaultBrowser === undefined) {
      throw new ApiNotSupportedError('isDefaultBrowser')
    }
    return this.jsBridge.isDefaultBrowser()
  }

  getKilobytesSavedToday(): number {
    throw new ApiNotSupportedError(`getKilobytesSavedToday`)
  }

  getKilobytesSavedTodayAsResult(): Result<number> {
    try {
      return resultOk(this.getKilobytesSavedToday())
    } catch (error: unknown) {
      return resultError(error)
    }
  }

  triggerDataSavedReport(): void {
    throw new ApiNotSupportedError('triggerDataSavedReport')
  }

  async openDefaultBrowserDialog(): Promise<void> {
    if (this.jsBridge.openDefaultBrowserDialog === undefined) {
      throw new ApiNotSupportedError('openDefaultBrowserDialog')
    }

    this.jsBridge.openDefaultBrowserDialog()
  }

  async openNativeScreen(screenName: 'redeem_points' | 'earn_rewards'): Promise<void> {
    if (this.jsBridge.openNativeScreen === undefined) {
      throw new ApiNotSupportedError('openNativeScreen')
    }

    return this.jsBridge.openNativeScreen(screenName)
  }
}
